import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import type { ScheduleWarning } from '../../dtos/scheduling/schedule.dto';
import { WARNING_COPY, warningDates } from './scheduleUtils';

export interface WarningListProps {
  warnings: ScheduleWarning[];
  /** Shown under NOT_ON_CARE_TEAM */
  addToCareTeam?: boolean;
  onAddToCareTeamChange?: (value: boolean) => void;
}

/** Inline schedule warnings (SCH-05/07, SOC-06). They never block; the coordinator confirms. */
const WarningList: React.FC<WarningListProps> = ({ warnings, addToCareTeam, onAddToCareTeamChange }) => {
  if (!warnings.length) return null;
  return (
    <ul className="space-y-2" aria-live="polite">
      {warnings.map((w, i) => {
        const copy = WARNING_COPY[w.code] || { title: w.code };
        const info = w.severity === 'info';
        return (
          <li key={`${w.code}-${i}`} className={`rounded-md border p-3 text-sm ${info ? 'border-sky-200 bg-sky-50 text-sky-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
            <div className="flex gap-2">
              {info ? <Info size={16} className="mt-0.5 shrink-0" /> : <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <div className="font-medium">{copy.title}</div>
                <div>{w.message}{warningDates(w) ? ` — ${warningDates(w)}` : ''}</div>
                {copy.hint && <div className="mt-0.5 text-xs opacity-80">{copy.hint}</div>}
                {w.code === 'SOC_INCOMPLETE' && Array.isArray(w.missing) && (
                  <div className="mt-1 text-xs">Missing: {w.missing.join(', ')}</div>
                )}
                {w.code === 'NOT_ON_CARE_TEAM' && onAddToCareTeamChange && (
                  <label className="mt-2 flex min-h-[32px] items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={addToCareTeam !== false}
                      onChange={(e) => onAddToCareTeamChange(e.target.checked)}
                    />
                    Add to the care team when I save
                  </label>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default WarningList;
